
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/lib/supabase";
import { format } from "date-fns";
import { Subscription } from "@/pages/pricing/hooks/subscription/types";

export function useSubscriptionManagement(
  isSubscribed: boolean,
  currentSubscription: Subscription | null,
  refreshSubscription?: (forceRefresh?: boolean) => Promise<boolean | void>
) {
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();

  // Format subscription dates if available
  const startDate = currentSubscription?.created_at 
    ? new Date(currentSubscription.created_at).toLocaleDateString() 
    : 'N/A';
  
  // Determine subscription status and display values
  const isCancelled = currentSubscription?.subscription_status === 'cancelled';
  
  // Use isSubscribed to determine if they have active Pro access
  // This correctly handles both active subscriptions and cancelled ones in the grace period
  const hasActiveProAccess = isSubscribed;
  
  const endDate = currentSubscription?.end_date ? new Date(currentSubscription.end_date) : null;
  const daysRemaining = endDate ? Math.ceil((endDate.getTime() - Date.now()) / (1000 * 60 * 60 * 24)) : 0;
  
  const billingOrEndDate = currentSubscription 
    ? isCancelled && currentSubscription.end_date
      ? new Date(currentSubscription.end_date).toLocaleDateString()
      : currentSubscription.created_at 
        ? new Date(new Date(currentSubscription.created_at).setMonth(
            new Date(currentSubscription.created_at).getMonth() + 1
          )).toLocaleDateString() 
        : 'N/A'
    : 'N/A';

  const handleCancelSubscription = async () => {
    if (!currentSubscription?.paypal_subscription_id) {
      toast({
        title: "Error",
        description: "No PayPal subscription ID found.",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);
    try {
      // Call Supabase edge function to cancel subscription
      const { data, error } = await supabase.functions.invoke('cancel-subscription', {
        body: { 
          subscriptionId: currentSubscription.paypal_subscription_id 
        }
      });

      if (error) throw error;

      if (data.success) {
        toast({
          title: "Subscription cancelled",
          description: data.endDate 
            ? `Your subscription has been cancelled and will remain active until ${format(new Date(data.endDate), 'MMMM d, yyyy')}.` 
            : "Your subscription has been cancelled and will be active until the end of your billing period.",
        });
        // Refresh subscription status if available
        if (refreshSubscription) {
          await refreshSubscription(true);
        }
      } else {
        throw new Error(data.message || "Failed to cancel subscription");
      }
    } catch (error) {
      console.error("Error cancelling subscription:", error);
      toast({
        title: "Error",
        description: "Failed to cancel your subscription. Please try managing it directly through PayPal.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpgradeSubscription = () => {
    navigate("/pricing");
  };

  return {
    isLoading,
    hasActiveProAccess,
    isCancelled,
    startDate,
    billingOrEndDate,
    endDate,
    daysRemaining,
    handleCancelSubscription,
    handleUpgradeSubscription
  };
}
